import { nextTick, type Ref, watch, type WatchSource } from 'vue'

import { useFocusTrap as useVueUseFocusTrap } from '@vueuse/integrations/useFocusTrap'

// Trap Tab / Shift+Tab inside the modal root while `isOpen` is true, and
// restore focus to the element that opened the modal when it closes.
//
// Thin wrapper over `@vueuse/integrations/useFocusTrap` (which itself
// wraps the `focus-trap` package) so each modal can pass its own
// `isOpen` reactive source without managing `activate` / `deactivate`
// manually. Replaced the previous in-repo `useFocusTrap` + sibling
// `useReturnFocus` composables 2026-06-05; return-focus is the
// `returnFocusOnDeactivate: true` default on `focus-trap` and no longer
// needs its own composable.
//
// Call inside a Vue `setup()`:
//
//   const modalRoot = ref<HTMLElement | null>(null)
//   useFocusTrap(modalRoot, () => mainStore.showPortfolioTrackerModal)
//
//   <template>
//     <div ref="modalRoot" role="dialog" ...>
//
// Options chosen:
// - `escapeDeactivates: false` — each modal owns its own Escape handler
//   (`onKeyStroke('Escape', ...)`) that calls the appropriate close
//   action so state stays consistent. If `focus-trap` deactivated on
//   Escape independently, the trap would tear down but the modal would
//   stay rendered.
// - `clickOutsideDeactivates: false` — same reasoning for backdrop
//   clicks; each modal's backdrop handler owns the close action.
// - `allowOutsideClick: true` — don't `preventDefault()` outside-trap
//   clicks; our backdrop click handlers need to receive the event.
// - `returnFocusOnDeactivate` defaults to `true` and is what gives us
//   the return-focus behavior for free.
export function useFocusTrap(rootRef: Ref<HTMLElement | null>, isOpen: WatchSource<boolean>): void {
  const { activate, deactivate } = useVueUseFocusTrap(rootRef, {
    escapeDeactivates: false,
    clickOutsideDeactivates: false,
    allowOutsideClick: true
  })

  watch(isOpen, async (open) => {
    if (open) {
      // `focus-trap` requires the container to be in the DOM at activate
      // time; wait one tick so the modal's `v-if`-driven content is
      // mounted before we activate.
      await nextTick()
      activate()
    } else {
      deactivate()
    }
  })
}
